import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import type { ReactNode } from "react";
import type { OSSchema } from "@sloe/schema";
import {
  Sparkles,
  ClipboardList,
  Plug,
  GitBranch,
  Settings,
  Menu,
  LayoutGrid,
  Gauge,
  Bot,
  Crown,
} from "lucide-react";
import AuditScreen from "./schema-renderer/AuditScreen";
import CommandCockpit from "./schema-renderer/CommandCockpit";
import { IntegrationHub } from "./schema-renderer/IntegrationHub";
import { EvolutionCenter } from "./schema-renderer/EvolutionCenter";
import SettingsScreen from "./SettingsScreen";
import { signIn, signOut } from "../lib/sloeAuthClient";
import { AssistantPage } from "./AssistantPage";
import { MissionControl } from "./MissionControl";
import { SchemaRenderer } from "./schema-renderer/SchemaRenderer";
import { ErrorBoundary } from "./ErrorBoundary";
import { UpdateToast } from "./UpdateToast";
import { AgenticOSWalkthrough } from "./AgenticOSWalkthrough";
import { DEFAULT_BRAND_COLOR } from "../lib/brand";
import { safeInitial } from "../lib/initial";
import { ASSISTANT_PROFILES, isAssistantHiddenFromNav } from "../agents/registry";
import type { AssistantProfile } from "../agents/registry";
import type { OperatingSystem } from "../lib/lab-data";
import { fetchEvolutionProposals } from "../lib/evolution";

// ─────────────────────────────────────────────────────────────────────────
// CodeOSShell — Phase 1 of the approved spec.
//
// A code-bundle Agentic Operating System (active_schema.bundle_type === "code") has NO OSSchema; it's
// a self-contained generated React app served at /_generated/<os_id>/. This
// shell wraps that iframe in the same Sloe chrome schema Operating Systems (Applications) get — a left nav
// with a "Workspace" group, an "Agents" group, and a "System" section + Settings
// footer — so the system surfaces are available on every Agentic Operating System type.
//
// The left nav has three groups, top to bottom:
//   1. "Workspace" — the customer's generated app, a SINGLE top-level entry
//      (the app name) that shows the iframe. Workspace switching lives inside
//      the generated app's own view picker, so the shell never turns the app
//      into a pile of peer-level entries. Old bundles still work because
//      their internal nav remains intact. All bridge messages are same-origin
//      + shape-checked.
//   2. "Agents" — the WORKER assistants (registry is_system === false:
//      Operator, Analyst). Each is a tabbed assistant page (Chat/Workspace/
//      Activity).
//   3. "System" — the president-tier assistant(s) (registry is_system === true:
//      The Architect) followed by Audit. The Architect owns the workspace-wide
//      structural surfaces, so Integrations + Evolution are TABS inside its
//      page (Chat/Integrations/Evolution/Activity) rather than standalone nav
//      entries. RATIONALE: connections are workspace-wide (os_mcp_connections
//      is per-workspace, so every agent already shares them at the data layer —
//      no point duplicating a Connections tab per agent), and evolution is
//      structural = the Architect's domain. The Architect is the "president"
//      (owns structure/connections/evolution); the other agents are staff.
//  - DUPLICATES the SchemaRenderer chrome markup (no shared layout extraction yet)
//    and restyles it DARK to match the generated app's design system.
//  - feeds the surfaces os.id (workspaceId). The assistant pages get a synthetic
//    OSSchema (screens:[] stops the schema.screens.find crash at Operator.tsx;
//    the tools are workspaceId/screen_data-keyed and unaffected).
// ─────────────────────────────────────────────────────────────────────────

// System nav surfaces the shell renders directly (not assistant pages):
// Integrations, Evolution, Audit, and Settings. (Operator lives in the Agents
// group; Integrations + Evolution were promoted OUT of The Architect's tabs into
// these standalone "Systems" screens — moved, not duplicated.)
type SystemId = "audit" | "settings" | "integrations" | "evolution" | "command";

// A single Workspace nav entry. The discriminated `kind` is what lets the list
// hold both iframe-backed screens and (future) shell-native React screens
// without branching the nav markup — add a kind, render it in the content slot.
type WorkspaceEntry =
  // The whole generated app under one top-level entry (always present).
  | { kind: "iframe_app"; id: "workspace"; label: string }
  // A legacy generated screen id announced by older bundles; click → sloe:navigate.
  | { kind: "iframe_screen"; id: string; label: string }
  // A screen this shell renders itself (the "Agents" group).
  | { kind: "shell_native"; id: string; label: string; render: () => ReactNode };

// The fixed id of the top-level customer-app entry (Group 1 "Workspace").
const APP_ENTRY_ID = "workspace";
// Mission Control — pinned first in Workspace and the default landing screen.
const MISSION_CONTROL_ID = "mission_control";

// Shell-native assistant entries are built in-component from ASSISTANT_PROFILES
// (one per assistant) because each render() closes over the live Agentic Operating System context
// (os, synthetic schema, brand). They split by registry `is_system`: workers
// land in the "Agents" group, system assistants in the "System" section.
const ASSISTANT_ENTRY_PREFIX = "assistant:";

type DeployedAgent = {
  id: string;
  title: string;
  description: string | null;
  schema_diff: {
    agent_id?: string;
    agent_name?: string;
    department?: string;
    skill_ids?: string[];
    studio_enabled?: boolean;
    studio_kind?: "code" | "content";
    required_screens?: string[];
    starter_tasks?: string[];
    recommended_integrations?: string[];
    success_metrics?: string[];
  } | null;
};

function specialistAssistantId(agentId: string): string {
  return `specialist_${String(agentId || "agent").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "agent"}`;
}

function deployedAgentProfile(agent: DeployedAgent): AssistantProfile | null {
  const diff = agent.schema_diff || {};
  const rawId = diff.agent_id || agent.id;
  const hasCodeStudio = rawId === "coding_agent";
  const hasContentStudio = rawId === "content_creator" || rawId === "ugc_creator_manager";
  const id = specialistAssistantId(rawId);
  const name = diff.agent_name || agent.title.replace(/^Deploy\s+/i, "") || "Specialist Agent";
  const department = diff.department || "Specialist";
  const skillIds = Array.isArray(diff.skill_ids) && diff.skill_ids.length
    ? diff.skill_ids
    : ["workspace_data_read", "screen_triage", "outlook_projection", "schema_evolution", "failure_diagnosis"];
  return {
    id,
    display_name: name,
    role_description: agent.description || `Approved ${department.toLowerCase()} specialist for the Agentic Operating System.`,
    engine_badge: `${department} specialist`,
    backend: { kind: "harness_chat", provider: "gemini" },
    harness_role: "operator",
    capabilities: [
      `Read ${department.toLowerCase()} context from the Agentic Operating System`,
      "Triage relevant records",
      "Project risks and next moves",
      "Propose specialist improvements for approval",
    ],
    starter_prompts: [
      ...(Array.isArray(diff.starter_tasks) && diff.starter_tasks.length
        ? diff.starter_tasks.slice(0, 3).map((task) => ({ label: task.slice(0, 34), prompt: task }))
        : [
            { label: "What should you review?", prompt: "Review your specialty in the Agentic Operating System and tell me what deserves attention first." },
            { label: "Find risks", prompt: "What risks do you see in your area, based on the Agentic Operating System data?" },
            { label: "Suggest an improvement", prompt: "What would you improve in the Agentic Operating System for your specialty?" },
          ]),
    ],
    skill_ids: skillIds,
    studio_enabled: diff.studio_enabled === true || hasCodeStudio || hasContentStudio,
    studio_kind: diff.studio_kind || (hasCodeStudio ? "code" : hasContentStudio ? "content" : undefined),
    workspace_blueprint: {
      required_screens: diff.required_screens || [],
      starter_tasks: diff.starter_tasks || [],
      recommended_integrations: diff.recommended_integrations || [],
      success_metrics: diff.success_metrics || [
        "Uses the right specialist skill for the task.",
        "Shows useful results in chat and preserves repeatable work in Workspace.",
        "Explains missing data, screens, or permissions when blocked.",
      ],
    },
    is_system: false,
  };
}

function inferAgentLibraryDepartmentFromAssistantId(assistantId: string): string | undefined {
  const text = String(assistantId || "").toLowerCase();
  if (/content|ugc|social|growth/.test(text)) return "Content";
  if (/coding|code|frontend|backend|website|repo/.test(text)) return "Code";
  return undefined;
}

// Shell-rendered System surfaces (below the system assistants): Integrations +
// Evolution (promoted out of The Architect's tabs) and Audit.
const SYSTEM_ITEMS: { id: SystemId; label: string; icon: typeof Sparkles }[] = [
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "evolution", label: "Evolution", icon: GitBranch },
  { id: "audit", label: "Audit", icon: ClipboardList },
];
// Founder-only System entry, appended to SYSTEM_ITEMS for the founder. NOT a
// security boundary — visibility is convenience; the wall is requireFounder on
// /api/platform/metrics, which 403s a non-founder regardless of the UI.
const COMMAND_ITEM: { id: SystemId; label: string; icon: typeof Sparkles } = {
  id: "command", label: "Command", icon: Gauge,
};
const SYSTEM_IDS: SystemId[] = ["audit", "settings", "integrations", "evolution", "command"];
const isSystemId = (id: string): id is SystemId => (SYSTEM_IDS as string[]).includes(id);

export function CodeOSShell({
  os,
  userFirstName,
  schema,
  showCommand = false,
  user,
}: {
  os: OperatingSystem;
  userFirstName?: string;
  schema?: OSSchema;
  // The real session user, forwarded to the Settings Profile section (the shell
  // otherwise had no user, so Profile showed "—").
  user?: { id: string; email: string } | null;
  // Server-computed (isFounder AND current OS is the HQ OS) — see
  // /api/os-by-slug show_command. Gates the Command cockpit's nav + render to
  // the founder's HQ OS only. Convenience gate: the real wall is requireFounder
  // on /api/platform/metrics.
  showCommand?: boolean;
}) {
  // The Command cockpit appears in the System nav only for the founder inside
  // the HQ OS. This is convenience: the endpoint (requireFounder) is the actual
  // access control.
  const systemItems = showCommand ? [...SYSTEM_ITEMS, COMMAND_ITEM] : SYSTEM_ITEMS;
  // activeId is the id of the currently-open nav entry — Mission Control
  // (default landing), the customer-app id (APP_ENTRY_ID), an assistant entry
  // id, or a SystemId. Single source of truth for highlight + which content
  // slot renders.
  // A connector OAuth round-trip returns to the app root with ?screen=integrations
  // plus ?mcp_connected=… (or ?mcp_error=…). Open the Integrations screen on that
  // load so the user lands back where they clicked Connect — IntegrationHub then
  // reads the param, marks the card Connected, and shows the "talk to your agent"
  // prompt. Without this the app would default to Mission Control and the return
  // would be lost.
  const [activeId, setActiveId] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      if (p.get("screen") === "integrations") return "integrations";
      if (p.get("mcp_connected") || p.get("mcp_error")) return "integrations";
    }
    return MISSION_CONTROL_ID;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Deep-link intent from Mission Control into an assistant page (open a tab /
  // prefill chat). Stored in a ref (read lazily by the assistant render closure)
  // plus a nonce that bumps to remount the page so the intent is honored.
  const assistantIntentRef = useRef<{ assistantId: string; tab?: string; prompt?: string } | null>(null);
  const [agentLibraryIntent, setAgentLibraryIntent] = useState<{ nonce: number; department?: string } | null>(null);
  const [navNonce, setNavNonce] = useState(0);
  const [authExpired, setAuthExpired] = useState(false);
  const [deployedAgents, setDeployedAgents] = useState<DeployedAgent[]>([]);
  // Charter "mentioned tools" — drives the IntegrationHub highlight on the
  // standalone Integrations system screen (replaces the per-Architect-tab fetch
  // that used to live in AssistantPage). Ungated: the shell always owns this
  // workspace-wide surface, so there's no per-agent gate to apply.
  const [charterToolsMentioned, setCharterToolsMentioned] = useState<string[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // SF-1 — generated-app load watchdog. If the bundle 404s (generation not
  // finished), 500s, or its JS crashes before rendering, the pane would sit
  // permanently blank. Track load state; a nonce bump reloads the iframe.
  const [iframeState, setIframeState] = useState<"loading" | "ready" | "failed">("loading");
  const [iframeNonce, setIframeNonce] = useState(0);

  const brandColor = os.brand_color || DEFAULT_BRAND_COLOR;
  const osName = os.os_name || "Your Agentic Operating System";
  const rawActiveSchema = (os.active_schema || {}) as any;
  const schemaBundleType = rawActiveSchema?.bundle_type || (schema ? "schema" : null);
  const schemaStandardVersion =
    (schema?.metadata as any)?.schema_standard_version ||
    rawActiveSchema?.metadata?.schema_standard_version ||
    null;
  const schemaScreenCount =
    (schema?.screens?.length || 0) ||
    (Array.isArray(rawActiveSchema?.screens) ? rawActiveSchema.screens.length : 0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEvolutionProposals({
          workspaceId: os.id,
          statuses: ["applied"],
          type: "agent_deployment",
        });
        if (!cancelled) setDeployedAgents((data as DeployedAgent[]) || []);
      } catch (err) {
        console.warn("[CodeOSShell] failed to load deployed agents:", err);
        if (!cancelled) setDeployedAgents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [os.id]);

  useEffect(() => {
    if (!os.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/charter/check/${encodeURIComponent(os.id)}`, { credentials: "include" });
        if (!res.ok) return;
        const json = await res.json().catch(() => null);
        if (cancelled) return;
        setCharterToolsMentioned(Array.isArray(json?.tools_mentioned) ? json.tools_mentioned : []);
      } catch {
        if (!cancelled) setCharterToolsMentioned([]);
      }
    })();
    return () => { cancelled = true; };
  }, [os.id]);

  // Synthetic schema for the Operator. Code Operating Systems (Applications) have no OSSchema; the Operator
  // tools read screen_data via workspaceId (not the schema), but the component
  // reads metadata.brand_color / metadata.operator and calls schema.screens.find
  // — so an empty screens array is what stops that crash.
  const synthSchema = useMemo<OSSchema>(
    () => schema ?? ({
      id: os.id,
      version: "code",
      metadata: {
        name: osName,
        industry_shape: os.industry_shape || "",
        brand_color: brandColor,
        created_at: "",
        operator: {},
      },
      data_models: {},
      screens: [],
      navigation: { items: [] },
    }),
    [schema, os.id, osName, os.industry_shape, brandColor],
  );

  const availableAssistantIds = useMemo(() => {
    const deployedProfiles = deployedAgents
      .map(deployedAgentProfile)
      .filter((p): p is AssistantProfile => !!p);
    return new Set<string>([...ASSISTANT_PROFILES, ...deployedProfiles].map((profile) => profile.id));
  }, [deployedAgents]);

  const isAssistantAvailable = useCallback((assistantId: string) => availableAssistantIds.has(assistantId), [availableAssistantIds]);

  // ─── Re-auth net ───
  // Code-Agentic Operating System data fetches inside the iframe 401 when the cookie lapses, and the
  // generated app's own error UI is naive (raw JSON). Since this shell is
  // same-origin with the iframe, poll /api/auth/me on an interval + on tab
  // refocus; if the session is gone, cover the shell with a branded re-auth
  // overlay routing back to the existing sign-in (a reload re-enters App's
  // bootstrap, which renders the lock screen with no session).
  const expiredRef = useRef(false);
  useEffect(() => {
    expiredRef.current = authExpired;
  }, [authExpired]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled || expiredRef.current) return;
      try {
        const r = await fetch("/api/auth/me", { credentials: "include" });
        if (cancelled) return;
        if (!r.ok) {
          if (r.status === 401) setAuthExpired(true);
          return; // transient non-401 (e.g. 5xx) — don't lock the user out
        }
        const j = await r.json().catch(() => null);
        if (cancelled) return;
        if (!j?.user) setAuthExpired(true);
      } catch {
        /* network blip — leave the user in place, try again next tick */
      }
    };
    const interval = window.setInterval(check, 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Deep-link from Mission Control into an assistant page: record the intent
  // (read by the assistant render closure) and bump navNonce so the content
  // wrapper remounts the page, letting its useState initializers honor the
  // tab/prompt. Plain nav clicks don't set an intent, so it never lingers.
  const navigateToAssistant = useCallback(
    (assistantId: string, target?: { tab?: string; prompt?: string }) => {
      // Integrations + Evolution are no longer Architect tabs — they're standalone
      // "Systems" nav screens. Catch every legacy deep-link still asking for those
      // tabs at this single chokepoint and route to the system screen instead of
      // the agent (so no call site needs editing; any chat prompt is moot here).
      if (target?.tab === "integrations" || target?.tab === "evolution") {
        setActiveId(target.tab);
        setSidebarOpen(false);
        return;
      }
      if (!isAssistantAvailable(assistantId)) {
        const department = inferAgentLibraryDepartmentFromAssistantId(assistantId);
        if (department) {
          setAgentLibraryIntent({ nonce: Date.now(), department });
        }
        setActiveId(MISSION_CONTROL_ID);
        setNavNonce((n) => n + 1);
        setSidebarOpen(false);
        return;
      }
      assistantIntentRef.current = { assistantId, ...target };
      setActiveId(`${ASSISTANT_ENTRY_PREFIX}${assistantId}`);
      setNavNonce((n) => n + 1);
      setSidebarOpen(false);
    },
    [isAssistantAvailable],
  );

  const navigateToMissionControl = useCallback(() => {
    setActiveId(MISSION_CONTROL_ID);
    setSidebarOpen(false);
  }, []);

  const navigateToAgentLibrary = useCallback((department?: string) => {
    setAgentLibraryIntent({ nonce: Date.now(), department });
    setActiveId(MISSION_CONTROL_ID);
    setNavNonce((n) => n + 1);
    setSidebarOpen(false);
  }, []);

  // ─── Workspace nav groups ───
  // Group 0: Mission Control — pinned first, the default landing. Decision-first
  // dashboard composed from existing reads; hands off into assistant pages.
  const missionControlEntry = useMemo<WorkspaceEntry>(
    () => ({
      kind: "shell_native",
      id: MISSION_CONTROL_ID,
      label: "Mission Control",
      render: () => (
        <MissionControl
          os={{ id: os.id, name: osName }}
          userFirstName={userFirstName}
          brandColor={brandColor}
          schemaStandardVersion={schemaStandardVersion}
          schemaScreenCount={schemaScreenCount}
          schemaBundleType={schemaBundleType}
          onNavigateAssistant={navigateToAssistant}
          openAgentLibraryIntent={agentLibraryIntent}
        />
      ),
    }),
    [os.id, osName, userFirstName, brandColor, schemaStandardVersion, schemaScreenCount, schemaBundleType, navigateToAssistant, agentLibraryIntent],
  );
  // Group 1: the customer's app. Code bundles render an iframe-backed app.
  // Template/schema Operating Systems (Applications) render their workspace
  // screens directly in this shell.
  const appEntry = useMemo<WorkspaceEntry>(
    () =>
      schema
        ? {
            kind: "shell_native",
            id: APP_ENTRY_ID,
            label: osName,
            render: () => (
              <SchemaRenderer
                schema={schema}
                workspaceId={os.id}
                userFirstName={userFirstName}
                user={user ?? null}
                onAskOperator={(prompt) => navigateToAssistant("operator", { tab: "chat", prompt })}
                embedded
              />
            ),
          }
        : { kind: "iframe_app", id: APP_ENTRY_ID, label: osName },
    [schema, os.id, osName, userFirstName, user, navigateToAssistant],
  );
  // Shell-native assistant entries, one per registry profile, each closed over
  // the live Agentic Operating System context. Both groups render the same AssistantPage; only their
  // nav placement (and the page's tab set, via profile.is_system) differs.
  const { workerAgentEntries, systemAgentEntries } = useMemo(() => {
    const deployedProfiles = deployedAgents
      .map(deployedAgentProfile)
      .filter((p): p is AssistantProfile => !!p);
    const allProfiles: AssistantProfile[] = [...ASSISTANT_PROFILES, ...deployedProfiles];
    const toEntry = (profile: AssistantProfile): WorkspaceEntry => ({
      kind: "shell_native",
      id: `${ASSISTANT_ENTRY_PREFIX}${profile.id}`,
      label: profile.display_name,
      render: () => {
        // Honor a Mission Control deep-link if it targets this assistant; the
        // content wrapper's navNonce key remounts the page so these land.
        const intent = assistantIntentRef.current;
        const applies = intent?.assistantId === profile.id;
        const consumeInitialIntent = applies
          ? () => {
              if (assistantIntentRef.current?.assistantId === profile.id) {
                assistantIntentRef.current = null;
              }
            }
          : undefined;
        return (
          <AssistantPage
            profile={profile}
            os={{ id: os.id }}
            synthSchema={synthSchema}
            userFirstName={userFirstName}
            brandColor={brandColor}
            initialTab={applies ? intent?.tab : undefined}
            initialPrompt={applies ? intent?.prompt : undefined}
            onConsumeInitialIntent={consumeInitialIntent}
            onAskOperator={(prompt) => navigateToAssistant("operator", { tab: "chat", prompt })}
            onNavigateAssistant={navigateToAssistant}
            isAssistantAvailable={isAssistantAvailable}
            onOpenAgentLibrary={navigateToAgentLibrary}
            onOpenMissionControl={navigateToMissionControl}
          />
        );
      },
    });
    return {
      // Group 2 ("Agents"): worker assistants — Chat/Workspace/Activity tabs.
      workerAgentEntries: allProfiles.filter((p) => !p.is_system).map(toEntry),
      // Group 3 ("System"): president-tier assistants (The Architect) — render
      // above Audit, with Chat/Integrations/Evolution/Activity tabs.
      systemAgentEntries: allProfiles.filter((p) => p.is_system).map(toEntry),
    };
  }, [deployedAgents, os.id, synthSchema, userFirstName, brandColor, navigateToAssistant, isAssistantAvailable]);

  // Flat lookup across every Workspace-ish entry so `activeId` resolves to one
  // (drives the highlight, breadcrumb, and which content slot renders).
  const allWorkspaceEntries = useMemo<WorkspaceEntry[]>(
    () => [missionControlEntry, appEntry, ...workerAgentEntries, ...systemAgentEntries],
    [missionControlEntry, appEntry, workerAgentEntries, systemAgentEntries],
  );

  const activeWorkspaceEntry = allWorkspaceEntries.find((e) => e.id === activeId);
  const isWorkspaceActive = !!activeWorkspaceEntry;
  // The iframe stays mounted whenever a Workspace entry is active OR a System
  // screen is active (so the app state survives System detours); it's only
  // replaced when a shell-native Workspace screen is active.
  const showIframe = !schema && (!activeWorkspaceEntry || activeWorkspaceEntry.kind !== "shell_native");

  const postToIframe = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
  }, []);

  // ─── Embed bridge ───
  // Same-origin postMessage from the generated app. Every message is origin- and
  // shape-validated. Old bundles never send these → the fallback list stands.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return; // same-origin only
      const d = e.data as any;
      if (!d || typeof d !== "object" || typeof d.type !== "string" || !d.type.startsWith("sloe:")) return;
      switch (d.type) {
        case "sloe:ready": {
          setIframeState("ready"); // definitive success signal from the app
          break;
        }
        case "sloe:screen":
          break;
        case "sloe:session_expired":
          setAuthExpired(true);
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // SF-1 — watchdog for the generated-app iframe. If neither a sloe:ready
  // message, a mounted app root (onLoad), nor a hard onError arrives within the
  // grace window, the bundle 404'd / crashed before rendering → flip to a retry
  // overlay instead of a permanently blank pane. Re-arms on each nonce (retry).
  useEffect(() => {
    if (schema) return; // iframe (code-bundle) path only
    setIframeState("loading");
    const t = setTimeout(() => {
      setIframeState((s) => (s === "ready" ? s : "failed"));
    }, 9000);
    return () => clearTimeout(t);
  }, [iframeNonce, schema]);

  const onSelectWorkspace = useCallback(
    (entry: WorkspaceEntry) => {
      setActiveId(entry.id);
      setSidebarOpen(false);
      if (entry.kind === "iframe_screen") postToIframe({ type: "sloe:navigate", id: entry.id });
    },
    [postToIframe],
  );

  // Nav button styling, split so the Workspace row, Agents, and System entries
  // can reuse the look without clashing horizontal padding.
  const navButtonBase = "flex items-center gap-2 py-1.5 rounded-md text-[13px] text-left transition-colors";
  const navButtonState = (active: boolean) =>
    active
      ? "bg-white/10 text-white font-medium"
      : "text-white/55 hover:bg-white/[0.06] hover:text-white";
  const navButtonClass = (active: boolean) =>
    `w-full px-2 mb-0.5 ${navButtonBase} ${navButtonState(active)}`;

  const breadcrumbLabel = activeWorkspaceEntry
    ? activeWorkspaceEntry.label
    : activeId === "audit"
      ? "Audit"
      : activeId === "settings"
        ? "Settings"
        : activeId === "integrations"
          ? "Integrations"
          : activeId === "evolution"
            ? "Evolution"
            : activeId === "command"
              ? "Command"
            : osName;

  return (
    <div className="fixed inset-0 flex bg-[#0A0A0A] text-white antialiased font-sans">
      {/* SIDEBAR */}
      <aside
        className={`w-60 shrink-0 flex flex-col bg-[#121212] border-r border-white/10 fixed inset-y-0 left-0 z-50 transition-transform md:static md:translate-x-0 md:z-auto md:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand */}
        <div className="h-14 flex items-center gap-2.5 px-4 border-b border-white/10">
          <div className="w-6 h-6 rounded-md shrink-0" style={{ backgroundColor: brandColor }} />
          <div className="flex-1 text-[13px] font-semibold text-white truncate">{osName}</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {/* Group 1 — Workspace: Mission Control (pinned first) + the
              customer's app. Workspace switching lives inside the app. */}
          <div className="px-2 mb-1 text-[11px] font-medium text-white/40 uppercase tracking-wider">
            Workspace
          </div>
          <button
            onClick={() => onSelectWorkspace(missionControlEntry)}
            className={navButtonClass(activeId === MISSION_CONTROL_ID)}
          >
            <Gauge size={14} className="shrink-0" />
            <span className="truncate">Mission Control</span>
          </button>
          <button
            onClick={() => onSelectWorkspace(appEntry)}
            className={navButtonClass(activeId === APP_ENTRY_ID)}
          >
            <LayoutGrid size={14} className="shrink-0" />
            <span className="truncate">{appEntry.label}</span>
          </button>

          {/* Group 2 — Agents: worker assistants (is_system === false) */}
          <div className="px-2 mt-6 mb-1 text-[11px] font-medium text-white/40 uppercase tracking-wider">
            Agents
          </div>
          {workerAgentEntries.map((entry) => (
            <button
              key={entry.id}
              onClick={() => onSelectWorkspace(entry)}
              className={navButtonClass(activeId === entry.id)}
            >
              <Bot size={14} className="shrink-0" />
              <span className="truncate">{entry.label}</span>
            </button>
          ))}

          {/* Group 3 — Systems: the president-tier assistant (The Architect) then
              the shell's own system surfaces — Integrations, Evolution, and Audit
              (Integrations + Evolution moved out of The Architect's tabs into
              these standalone screens). Settings lives in the footer. */}
          <div className="px-2 mt-6 mb-1 text-[11px] font-medium text-white/40 uppercase tracking-wider">
            Systems
          </div>
          {systemAgentEntries
            .filter((entry) => !isAssistantHiddenFromNav(entry.id.slice(ASSISTANT_ENTRY_PREFIX.length)))
            .map((entry) => (
            <button
              key={entry.id}
              onClick={() => onSelectWorkspace(entry)}
              className={navButtonClass(activeId === entry.id)}
            >
              <Crown size={14} className="shrink-0" />
              <span className="truncate">{entry.label}</span>
            </button>
          ))}
          {systemItems.map((item) => {
            const active = activeId === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveId(item.id);
                  setSidebarOpen(false);
                }}
                className={navButtonClass(active)}
              >
                <Icon size={14} className="shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer / settings */}
        <div className="border-t border-white/10 p-3">
          <button
            onClick={() => {
              setActiveId("settings");
              setSidebarOpen(false);
            }}
            className={navButtonClass(activeId === "settings")}
          >
            <Settings size={14} className="shrink-0" />
            <span>Settings</span>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay — tap to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* MAIN */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 shrink-0 flex items-center justify-between px-4 md:px-6 border-b border-white/10 bg-[#121212]">
          <div className="flex items-center gap-2 text-[13px] text-white/50 min-w-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-1.5 -ml-1 rounded-md text-white/70 hover:bg-white/10"
              aria-label="Open navigation"
            >
              <Menu size={18} />
            </button>
            <span className="truncate hidden sm:inline">{osName}</span>
            <span className="text-white/30 hidden sm:inline">/</span>
            <span className="text-white font-medium truncate">{breadcrumbLabel}</span>
          </div>
          <button
            onClick={() => navigateToAssistant("operator")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-colors"
          >
            <Sparkles size={13} />
            Operator
          </button>
        </header>

        {/* Content — `relative` anchor so the iframe / system panels fill via
            `absolute inset-0` instead of a percentage-height chain. iOS Safari
            (the installed PWA's WebView) fails to resolve `height:100%` on a
            replaced element (iframe) inside a flex item, collapsing it to its
            intrinsic ~150px → partial render + nested/double scrolling.
            Absolute-fill resolves against this box directly; `overflow-hidden`
            keeps ONE scroll context — the iframe's own app owns its scroll. */}
        <div className="flex-1 min-h-0 relative overflow-hidden">
          {/* The generated app: ALWAYS mounted so the app's internal state
              survives System detours. Hidden (not unmounted) when a System or
              shell-native screen shows. Same sandbox the bare-iframe branch
              used; same-origin so its fetch('/api/...') carries the cookie;
              os_id is baked into the HTML. */}
          {!schema && (
            <iframe
              key={iframeNonce}
              ref={iframeRef}
              src={`/_generated/${os.id}/index.html`}
              title={osName}
              className="absolute inset-0 w-full h-full"
              style={{ border: 0, visibility: showIframe ? "visible" : "hidden" }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              onError={() => setIframeState("failed")}
              onLoad={() => {
                // onLoad ALSO fires for a 404 error page, so it is not proof of
                // success on its own. Treat it as ready ONLY if the generated
                // app actually mounted its root (#root) — a 404/crash page has
                // none. sloe:ready remains the fast definitive signal; the
                // watchdog catches the "blank, no signal at all" case.
                try {
                  if (iframeRef.current?.contentDocument?.getElementById("root")) setIframeState("ready");
                } catch { /* same-origin, shouldn't throw — ignore */ }
              }}
            />
          )}
          {!schema && showIframe && iframeState === "failed" && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/95 p-6">
              <div className="max-w-sm text-center">
                <p className="text-slate-200 font-medium mb-1">Your app is still finishing setup</p>
                <p className="text-slate-400 text-sm mb-4">
                  It didn't finish loading — this can happen right after a deploy. Give it a moment and retry.
                </p>
                <button
                  onClick={() => setIframeNonce((n) => n + 1)}
                  className="px-4 py-2 rounded-lg bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Shell-native Workspace screens (Mission Control + assistant pages)
              over the hidden iframe. The screen owns its own dark chrome and
              scroll, so this is a neutral full-bleed fill (no forced light
              surface like the System slot below). The key includes navNonce so a
              Mission Control deep-link remounts the target assistant page and its
              initialTab/initialPrompt land. */}
          {isWorkspaceActive && activeWorkspaceEntry?.kind === "shell_native" && (
            <div key={`shellnative-${activeId}-${navNonce}`} className="absolute inset-0 overflow-hidden bg-[#0A0A0A]">
              {activeWorkspaceEntry.render()}
            </div>
          )}

          {/* System surfaces keep their own (light) styling; wrap them in a
              neutral surface so they read correctly inside the dark chrome.
              Rendered over the (hidden) iframe when a System screen is active:
              Integrations, Evolution, Audit, and Settings. */}
          {isSystemId(activeId) && (
            <div className="absolute inset-0 overflow-y-auto bg-zinc-50 text-zinc-900">
              {activeId === "command" ? (
                // Founder-in-HQ-OS only. Visibility is gated above (systemItems),
                // and the endpoint (requireFounder) is the real wall; rendering
                // only when showCommand is defense-in-depth so a stray activeId
                // can't even mount the fetch outside the HQ OS.
                showCommand ? (
                  <ErrorBoundary scope="Command">
                    <CommandCockpit />
                  </ErrorBoundary>
                ) : null
              ) : activeId === "audit" ? (
                <ErrorBoundary scope="Audit">
                  <AuditScreen workspaceId={os.id} />
                </ErrorBoundary>
              ) : activeId === "integrations" ? (
                <ErrorBoundary scope="Integrations">
                  <IntegrationHub
                    workspaceId={os.id}
                    toolsMentioned={charterToolsMentioned}
                    onAskOperator={(prompt) => navigateToAssistant("operator", { tab: "chat", prompt })}
                    onAskArchitect={(prompt) => navigateToAssistant("operator", { tab: "chat", prompt })}
                  />
                </ErrorBoundary>
              ) : activeId === "evolution" ? (
                <ErrorBoundary scope="the Evolution Center">
                  <EvolutionCenter workspaceId={os.id} onRefine={() => navigateToAssistant("operator", { tab: "chat" })} />
                </ErrorBoundary>
              ) : (
                <ErrorBoundary scope="Settings">
                  <SettingsScreen
                    os={{ id: os.id, name: osName, slug: os.subdomain_slug || "", brand_color: brandColor }}
                    user={user ?? null}
                    userFirstName={userFirstName}
                    onSignOut={async () => {
                      const ok = await signOut(window.location.origin + "/");
                      if (!ok) window.alert("Couldn't sign out — the sign-in service didn't confirm. You're still signed in on this device. Try again.");
                    }}
                    onOpenIntegrations={() => navigateToAssistant("architect", { tab: "integrations" })}
                  />
                </ErrorBoundary>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Re-auth overlay — branded, dark, covers the whole shell */}
      {authExpired && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6">
          <div className="w-full max-w-[380px] flex flex-col items-center text-center bg-[#101010] border border-white/10 rounded-2xl p-10">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-[#0a0a0a] text-xl font-bold mb-5"
              style={{ background: brandColor }}
            >
              {safeInitial(osName, "O")}
            </div>
            <h1 className="text-[20px] font-semibold mb-2">Your session expired</h1>
            <p className="text-[14px] text-white/60 leading-relaxed mb-6">
              For your security we signed you out of {osName}. Sign in again to pick up where you left off.
            </p>
            <button
              type="button"
              onClick={() => signIn(window.location.href)}
              className="w-full rounded-lg py-3 text-[15px] font-semibold text-[#0a0a0a]"
              style={{ background: brandColor }}
            >
              Sign in again
            </button>
          </div>
        </div>
      )}

      {/* New-deploy toast (controllerchange → tap to reload; auto-reload only
          when the page is fresh/hidden — see public/register-sw.js). */}
      <UpdateToast theme="dark" accent={brandColor} />
      <AgenticOSWalkthrough
        osId={os.id}
        osName={osName}
        accent={brandColor}
        onOpenMissionControl={() => setActiveId(MISSION_CONTROL_ID)}
        onOpenIntegrations={() => navigateToAssistant("architect", { tab: "integrations" })}
        onOpenOperator={() => navigateToAssistant("operator", { tab: "chat" })}
        onOpenReceipts={() => setActiveId(MISSION_CONTROL_ID)}
      />
    </div>
  );
}
